<template>
  <article class="br-doc-item">
    <div class="br-doc-item__header">
      <div class="br-doc-item__header-names">
        <p v-if="itemDisplayNameLocal" class="br-doc-item__header-name">{{ itemDisplayNameLocal }}</p>
        <p v-if="itemDisplayNameEn" class="br-doc-item__header-name">{{ itemDisplayNameEn }}</p>
        <p
          v-if="!itemDisplayNameLocal && !itemDisplayNameEn"
          class="br-doc-item__header-name"
        >—</p>
      </div>
      <div v-if="showHeaderActions" class="br-doc-item__header-actions">
        <span v-if="isCurrentDisplay" class="br-doc-item__current-badge">
          {{ $t('lbl_EMPBE_BR_List_CurrentDisplay') }}
        </span>
        <button
          v-else-if="showSetAsCurrent"
          type="button"
          class="br-doc-item__set-current"
          :disabled="!(!isApproval)"
          :class="{'BRDiableBtn':showEditName}"
          @click="$emit('set-as-current', item)"
        >
          {{ $t('lbl_EMPBE_BR_List_SetAsCurrent') }}
        </button>
        <button :showEditName="showEditName"
          type="button"
          class="br-doc-item__edit-btn"
          :class="{'BRDiableBtn':showEditName}"
          :disabled="!(!isApproval&&showEditName)"
          :aria-label="$t('lbl_EMPBE_BR_RequestNameChange')"
          @click.stop="$emit('edit-name', item)"
        >
          <img
            :src="editNameIconUrl"
            class="br-doc-item__edit-icon"
            width="18"
            height="18"
            alt=""
          />
        </button>
      </div>
    </div>

    <div class="br-doc-item__divider"></div>

    <div class="br-doc-item__body">
      <button type="button" class="br-doc-item__thumb" @click="$emit('preview', item)">
        <div class="br-doc-item__thumb-inner">
          <img
            v-if="thumbUrl && isImageThumb"
            :src="thumbUrl"
            alt=""
            class="br-doc-item__thumb-img"
            @error="thumbLoadFailed = true"
          />
          <div v-else class="br-doc-item__thumb-placeholder">
            <feather type="file-text" :size="28" />
          </div>
        </div>
        <span
          v-if="thumbUrl && isImageThumb && !thumbLoadFailed"
          class="br-doc-item__zoom"
          aria-hidden="true"
        >
          <feather type="zoom-in" :size="24" />
        </span>
      </button>

      <div class="br-doc-item__fields">
        <div class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_Status') }}</span>
          <div class="br-doc-item__field-value br-doc-item__field-value--status">
            <BrStatusBadge
              :status-code="badgeStatus"
              :status-name="statusName"
              :label-key="badgeLabelKey"
              :appearance="variant"
            />
          </div>
        </div>

        <div class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_RegisteredName') }}</span>
          <p class="br-doc-item__field-value">{{ companyName }}</p>
        </div>

        <div class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_RegistrationNo') }}</span>
          <p class="br-doc-item__field-value">{{ registrationNo }}</p>
        </div>

        <div v-if="validationText" class="br-doc-item__field">
          <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_Validation') }}</span>
          <p class="br-doc-item__field-value">{{ validationText }}</p>
        </div>

        <div class="br-doc-item__field br-doc-item__field--submitted">
          <div class="br-doc-item__field-main">
            <span class="br-doc-item__field-label">{{ $t('lbl_EMPBE_BR_List_Field_SubmittedOn') }}</span>
            <p class="br-doc-item__field-value">{{ submittedOnText || '—' }}</p>
          </div>
          <span v-if="documentId" class="br-doc-item__id">{{ documentId }}</span>
        </div>
      </div>
    </div>

    <!-- 更名待审：来源已生效照提示（Figma Info） -->
    <div v-if="showNameChangeSourcePendingHint" class="br-doc-item__info">
      <img
        :src="infoIconUrl"
        class="br-doc-item__info-icon"
        width="20"
        height="20"
        alt=""
      />
      <p
        class="br-doc-item__info-message"
        v-html="$t('lbl_EMPBE_BR_NameChange_SourceUnderReviewBanner')"
      ></p>
    </div>

    <!-- 供应商：卡片内驳回原因 + 操作 -->
    <BrRejectBanner
      v-if="showRejectedBanner"
      :reject-message="rejectMessage"
      @contact="$emit('contact', item)"
      @reupload="$emit('reupload', item)"
      @cancel="onRejectDeleteClick"
    />
    <!-- 管理端底栏：Reject / Proceed（Figma 已驳回无 Cancel / Re-Upload） -->
    <div v-if="isApproval && hasAdminFooter" class="br-doc-item__admin-footer">
      <slot name="admin-footer" />
    </div>

    <BrExpirySoonBar v-if="isExpiredSoon" />
  </article>
</template>

<script>
import BrStatusBadge from './BrStatusBadge.vue';
import BrRejectBanner from './BrRejectBanner.vue';
import BrExpirySoonBar from './BrExpirySoonBar.vue';
import {
  BR_STATUS,
  formatBrDocumentId,
  formatBrItemValidityText,
  resolveBrItemCompanyName,
  resolveBrItemRegistrationNo,
  resolveBrItemSubmittedOnText,
  brDisplayNamesMatchCompany,
  getItemStatusCode,
  resolveBrBadgeStatus,
  resolveBrDisplayStatus,
  resolveBrRejectReasonText,
  resolveBrUiStatus,
  resolveBrAdminStatusLabelKey,
  shouldShowBrListExpiredSoonBar,
} from '../utils/brDocument';
import { brRuntimeState } from '../../../constants/businessRegistration';
import { BR_LICENSE_ASSETS } from '../../../constants/brLicenseAssets';

export default {
  name: 'BrDocumentListItem',
  components: { BrStatusBadge, BrRejectBanner, BrExpirySoonBar },
  props: {
    item: {
      type: Object,
      required: true,
    },
    displayNameEn: {
      type: String,
      default: '',
    },
    displayNameLocal: {
      type: String,
      default: '',
    },
    thumbUrl: {
      type: String,
      default: '',
    },
    /** supplier | approval | edit */
    variant: {
      type: String,
      default: 'supplier',
      validator: (value) => ['supplier', 'edit', 'approval'].includes(value),
    },
    /** 是否为「Current Display」区主导照 */
    isCurrentDisplay: {
      type: Boolean,
      default: false,
    },
    /** 是否允许展示更名铅笔（页面层判断） */
    canEditName: {
      type: Boolean,
      default: false,
    },
    hasAdminFooter: {
      type: Boolean,
      default: false,
    },
    /** 管理端临期判断用目标司列表；空则回退供应商 runtime documentList */
    expiryContextList: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      thumbLoadFailed: false,
    };
  },
  computed: {
    isApproval() {
      return this.variant === 'approval';
    },
    infoIconUrl() {
      return BR_LICENSE_ASSETS.infoIcon;
    },
    editNameIconUrl() {
      return BR_LICENSE_ASSETS.editNameIcon;
    },
    displayStatus() {
      return this.isApproval
        ? resolveBrUiStatus(this.item)
        : resolveBrDisplayStatus(this.item);
    },
    badgeStatus() {
      return this.isApproval
        ? resolveBrUiStatus(this.item)
        : resolveBrBadgeStatus(this.item);
    },
    statusName() {
      return this.item?.statusName || this.item?.StatusName || '';
    },
    /**
     * 上区（当前生效展示）：已审批及已生效
     * 下区（其他）已通过但未作当前展示：已审批
     * 待生效：已审批 (待生效)（走默认 Status_NotYetEffective）
     */
    badgeLabelKey() {
      const code = String(this.badgeStatus || '').toUpperCase();
      if (this.isApproval && (code === BR_STATUS.PENDING || code === BR_STATUS.PENDING_MANUAL)) {
        return resolveBrAdminStatusLabelKey(code);
      }
      if (code === BR_STATUS.APPROVED || code === BR_STATUS.READY) {
        return this.isCurrentDisplay
          ? 'lbl_EMPBE_BR_Status_Approved'
          : 'lbl_EMPBE_BR_Status_ApprovedOnly';
      }
      return '';
    },
    isRejected() {
      return this.displayStatus === BR_STATUS.REJECTED
        || getItemStatusCode(this.item) === BR_STATUS.REJECTED;
    },
    showRejectedBanner() {
      // 供应商驳回卡：Figma 1142:37555 — 粉横幅 + Add Document + Dismiss/取消
      //  /** supplier | approval | edit */
      return (this.variant=='edit'|| this.variant=='supplier')&& this.isRejected;
    },
    isApprovedEffective() {
      return resolveBrDisplayStatus(this.item) === BR_STATUS.APPROVED;
    },
    /** 本卡 DisplayName 是否已等于当前网上展台名 */
    matchesCurrentCompanyDisplayName() {
      return brDisplayNamesMatchCompany(
        this.item,
        this.displayNameEn,
        this.displayNameLocal,
      );
    },
    showSetAsCurrent() {
      // 更名「申请」合成卡不可设为当前；更名通过后的结果主档是真实执照，应可设
      if (this.isCurrentDisplay || this.isNameChangeRequestCard) {
        return false;
      }
      // 仅已生效，且 DisplayName 尚非当前展台名（Non-Company Name）
      if (!this.isApprovedEffective) {
        return false;
      }
      return !this.matchesCurrentCompanyDisplayName;
    },
    showEditName() {
      if (!this.canEditName || this.isNameChangeRequestCard) {
        return false;
      }
      // Figma 1170：Edit 仅已生效；待生效不可更名
      return this.isApprovedEffective;
    },
    showHeaderActions() {
      return this.isCurrentDisplay || this.showSetAsCurrent || this.showEditName;
    },
    isExpiredSoon() {
      // 产品：临期黄条仅「当前公司展示名称」那张已生效 BR
      if (!this.isCurrentDisplay) {
        return false;
      }
      const context = Array.isArray(this.expiryContextList) && this.expiryContextList.length
        ? this.expiryContextList
        : brRuntimeState.documentList;
      return shouldShowBrListExpiredSoonBar(this.item, context, { isCurrentDisplay: true });
    },
    /**
     * 原 BR 更名蓝条：仅当仍有「人工审批中」的更名申请挂在此来源上时显示。
     * 审批通过 / 驳回 / 来源已失效后必须消失。
     */
    showNameChangeSourcePendingHint() {
      const marked = !!(
        this.item?.isSourceOfPendingNameChange
        || this.item?.IsSourceOfPendingNameChange
      );
      if (!marked) {
        return false;
      }
      const displayStatus = resolveBrDisplayStatus(this.item);
      if (
        displayStatus === BR_STATUS.INVALID
        || displayStatus === BR_STATUS.REJECTED
        || displayStatus === BR_STATUS.CANCELLED
      ) {
        return false;
      }
      return true;
    },
    /** 更名申请合成卡（待审/驳回）；不含结果照 */
    isNameChangeRequestCard() {
      return !!(
        this.item?.isNameChangeRequestCard
        || this.item?.IsNameChangeRequestCard
      );
    },
    documentId() {
      return formatBrDocumentId(this.item);
    },
    companyName() {
      return resolveBrItemCompanyName(this.item);
    },
    registrationNo() {
      return resolveBrItemRegistrationNo(this.item);
    },
    validationText() {
      return formatBrItemValidityText(this.item, this.$t.bind(this));
    },
    submittedOnText() {
      return resolveBrItemSubmittedOnText(this.item);
    },
    itemDisplayNameEn() {
      return (
        this.item?.displayNameEn
        || this.item?.DisplayNameEn
        || this.displayNameEn
        || ''
      ).trim();
    },
    itemDisplayNameLocal() {
      const fromItem = (this.item?.displayNameZh || this.item?.DisplayNameZh || '').trim();
      if (fromItem) {
        return fromItem;
      }
      return (this.displayNameLocal || '').trim();
    },
    isImageThumb() {
      return this.thumbUrl && !/\.pdf($|\?)/i.test(this.thumbUrl);
    },
    rejectMessage() {
      return resolveBrRejectReasonText(this.item, this.$t.bind(this));
    },
  },
  watch: {
    thumbUrl() {
      this.thumbLoadFailed = false;
    },
  },
  methods: {
    onRejectDeleteClick() {
      this.$emit('cancel', this.item);
    },
  },
};
</script>
